import React from 'react';
import {
	Box,
	Button,
	Checkbox,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import { CopyCandidate } from './Stage3JuryPairs.types';

type Props = {
	busy: boolean;
	copyCandidates: CopyCandidate[];
	selectedCopyItemsCount: number;
	selectedCopyKeys: Record<string, boolean>;
	candidateKey: (item: CopyCandidate) => string;
	copySortMode: 'districts' | 'pairs' | 'statuses';
	setCopySortMode: (mode: 'districts' | 'pairs' | 'statuses') => void;
	onLoadCopyCandidates: () => void;
	onSelectAllCopyAllowed: () => void;
	onClearCopySelection: () => void;
	onRunCopy: () => void;
	onToggleCopySelection: (item: CopyCandidate) => void;
	getCandidateStatus: (item: CopyCandidate) => string;
	sortedCopyCandidates: CopyCandidate[];
	copyRunReport: any | null;
};

const Stage3DriveCopyTab = ({
	busy,
	copyCandidates,
	selectedCopyItemsCount,
	selectedCopyKeys,
	candidateKey,
	copySortMode,
	setCopySortMode,
	onLoadCopyCandidates,
	onSelectAllCopyAllowed,
	onClearCopySelection,
	onRunCopy,
	onToggleCopySelection,
	getCandidateStatus,
	sortedCopyCandidates,
	copyRunReport,
}: Props) => {
	return (
		<>
			<Typography variant='subtitle1'>Kopiowanie plików do komisji</Typography>
			<Typography variant='caption'>
				Źródło: API Google Drive. Fallback tylko scenariusz C (czasowy brak API).
			</Typography>
			<Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
				<Button
					variant='outlined'
					onClick={onLoadCopyCandidates}
					disabled={busy}
				>
					Wczytaj dane
				</Button>
				<Button
					variant='outlined'
					onClick={onSelectAllCopyAllowed}
					disabled={busy || copyCandidates.length === 0}
				>
					Zaznacz wszystkie możliwe
				</Button>
				<Button
					variant='outlined'
					onClick={onClearCopySelection}
					disabled={busy || copyCandidates.length === 0}
				>
					Odznacz wszystkie
				</Button>
				<Button
					variant='contained'
					onClick={onRunCopy}
					disabled={busy || selectedCopyItemsCount === 0}
				>
					Uruchom kopiowanie zaznaczonych ({selectedCopyItemsCount})
				</Button>
				<FormControl size='small'>
					<InputLabel id='copy-sort-mode-label'>Sortuj listę</InputLabel>
					<Select
						labelId='copy-sort-mode-label'
						label='Sortuj listę'
						value={copySortMode}
						onChange={(e) => setCopySortMode(e.target.value as 'districts' | 'pairs' | 'statuses')}
						sx={{ minWidth: 220 }}
					>
						<MenuItem value='districts'>Lista okręgów</MenuItem>
						<MenuItem value='pairs'>Lista komisji</MenuItem>
						<MenuItem value='statuses'>Lista statusów</MenuItem>
					</Select>
				</FormControl>
			</Box>
			{copyCandidates.length > 0 && (
				<Box sx={{ mt: 1 }}>
					<TableContainer
						component={Paper}
						sx={{ maxHeight: 340 }}
					>
						<Table
							size='small'
							stickyHeader
						>
							<TableHead>
								<TableRow>
									<TableCell padding='checkbox'>Kopiuj</TableCell>
									<TableCell>Okręg</TableCell>
									<TableCell>Komisja</TableCell>
									<TableCell>Kod pracy</TableCell>
									<TableCell>Status</TableCell>
									<TableCell>Plik źródłowy</TableCell>
									<TableCell>Status</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{sortedCopyCandidates.map((item) => {
									const key = candidateKey(item);
									const checked = Boolean(selectedCopyKeys[key]);
									return (
										<TableRow key={key}>
											<TableCell padding='checkbox'>
												<Checkbox
													checked={checked}
													disabled={!item.copy_allowed || busy}
													onChange={() => onToggleCopySelection(item)}
												/>
											</TableCell>
											<TableCell>{item.district_name}</TableCell>
											<TableCell>{item.jury_pair_name}</TableCell>
											<TableCell>{item.work_code}</TableCell>
											<TableCell>{item.current_status || '-BRAK-'}</TableCell>
											<TableCell>{item.source_relative_path || '-'}</TableCell>
											<TableCell>{getCandidateStatus(item)}</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>
			)}
			{copyRunReport && (
				<Box sx={{ mt: 1 }}>
					<details>
						<summary>Wynik kopiowania (JSON)</summary>
						<TextField
							fullWidth
							multiline
							minRows={8}
							value={JSON.stringify(copyRunReport, null, 2)}
							InputProps={{ readOnly: true }}
							sx={{ mt: 1 }}
						/>
					</details>
				</Box>
			)}
		</>
	);
};

export default Stage3DriveCopyTab;
